import React, { FC, useEffect, useRef, useState } from 'react'
import SALink from 'components/SALink'
import { getImageAltText, getImageSrc, getCleanCategory } from 'components/SA/Polaroid/helpers'
import { ArtworkObject } from './types'
import { useSelector } from 'react-redux'
import { getIsMobileTraffic } from 'selectors/shared/mobileTraffic'
import PolaroidInfo from './PolaroidInfo'
import styles from './ProductCarouselPolaroid.module.scss'
import { getGeoLocation } from 'selectors/user/cookie'
import { getGeoZoneDestinationCode } from 'lib/storeLocale/client'
import { capitalizeString } from 'lib/helpers'
type PolaroidProps = {
  artwork: ArtworkObject
  onImageClick?: (ev: React.SyntheticEvent<Record<string, any>>) => void
  onTitleClick?: (ev: React.SyntheticEvent<Record<string, any>>) => void
  polaroidRef?: React.RefObject<HTMLDivElement>
  index: number
  listId: string
  listName: string
  dataApi: string
  showMediumOnMaterial?: boolean
  showArtistName?: boolean
  isLazyLoading?: boolean
}

const Polaroid: FC<PolaroidProps> = ({
  artwork,
  onImageClick,
  onTitleClick,
  polaroidRef,
  index,
  listId,
  listName,
  dataApi,
  showMediumOnMaterial,
  showArtistName,
  isLazyLoading = true,
}) => {
  const {
    artworkId,
    baseImageUrl,
    productUrl,
    artistName,
    title,
    category,
    sku,
    originalListPrices,
  } = artwork
  const capitalizedArtworkTitle = capitalizeString(title)
  const cleanCategory = getCleanCategory(category)
  const capitalizedCleanCategory = capitalizeString(cleanCategory)
  /* Replacing connector */
  const isMobile = useSelector(getIsMobileTraffic)
  const geoLocation = useSelector(getGeoLocation)
  /* ============================================================== */
  const destinationZone = getGeoZoneDestinationCode(geoLocation?.countryCode)
  const listPrice = originalListPrices?.[destinationZone] || 0
  const imageRef = useRef<HTMLImageElement>(null)
  const [imageDimensions, setImageDimensions] = useState<{
    width: number | undefined
    height: number | undefined
  }>({
    width: undefined,
    height: undefined,
  })

  const shouldOpenNewDesktopPdpTab = !isMobile
  const imageSrc = getImageSrc({
    originalSrc: baseImageUrl,
    priority: false,
  })
  const imageAlt = getImageAltText({
    artistName,
    category,
  })

  useEffect(() => {
    if (imageRef.current) {
      const image = imageRef.current

      const resizeObserver = new ResizeObserver(() => {
        setImageDimensions({
          width: image.offsetWidth,
          height: image.offsetHeight,
        })
      })

      resizeObserver.observe(image)

      return () => {
        resizeObserver.disconnect()
      }
    }
  }, [])

  return (
    <div className={styles.carouselPolaroidBox} ref={polaroidRef}>
      <SALink
        href={productUrl}
        onClick={onImageClick}
        target={shouldOpenNewDesktopPdpTab ? '_blank' : ''}
        rel={capitalizedArtworkTitle}
        title={`${capitalizedArtworkTitle} ${capitalizedCleanCategory}`}
        data-api={dataApi}
        data-id={sku}
        data-name={title}
        data-position={index}
        data-list={listId}
        data-category={category}
        data-listid={listId}
        data-listname={listName}
        data-artid={artworkId}
        data-brand={artistName}
        data-price={listPrice}
      >
        <div className={styles.carouselPolaroid}>
          <img
            ref={imageRef}
            src={imageSrc}
            width={imageDimensions.width}
            height={imageDimensions.height}
            loading={isLazyLoading ? 'lazy' : undefined}
            alt={imageAlt}
            title={title}
          />
        </div>
      </SALink>

      <PolaroidInfo
        artwork={artwork}
        itemPosition={index}
        onTitleClick={onTitleClick}
        listId={listId}
        listName={listName}
        dataApi={dataApi}
        showMediumOnMaterial={showMediumOnMaterial}
        showArtistName={showArtistName}
      />
    </div>
  )
}

export default Polaroid
